/**
 * Hook para gerenciamento de contratos de um deal
 * CRUD completo com React Query e Supabase
 * 
 * RELEASE: v1.1.0 - 2026-09-03: bloqueios do Workflow usam aviso modal global
 * RELEASE: v1.0.0 - 2026-02-16
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { normalizePhoneIdentifier } from '@/lib/phone-utils';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';
import type { Contract, ContractStatus } from '@/types/contracts';

// Logger do módulo
const logger = createLogger('useContracts');

/**
 * Input para criar um contrato
 */
interface CreateContractInput {
  deal_id: string;
  template_id: string;
  title: string;
  content_html: string;
  variables_data: Record<string, string>;
  tenant_id: string;
  /** ID da versão efetivamente usada no momento da geração (rastreabilidade histórica) */
  used_template_version_id?: string | null;
  /** ID do contrato publicado que originou esta geração */
  published_contract_id?: string | null;
}

/**
 * Hook para listar contratos de um deal
 */
/**
 * Hook para listar contratos de um deal via RPC server-side
 * Substitui consulta direta à tabela contracts (padrão segurança por abstração)
 */
export function useContractsByDeal(dealId: string | null) {
  return useQuery({
    queryKey: ['contracts', 'deal', dealId],
    queryFn: async () => {
      if (!dealId) return [];
      
      const { data, error } = await supabase
        .rpc('get_contracts_by_deal', { p_deal_id: dealId });

      if (error) {
        logger.error('Erro ao buscar contratos do deal via RPC', error);
        throw error;
      }

      // Mapeia resultado flat da RPC para interface Contract
      return (data || []).map((row: any) => ({
        ...row,
        contract_templates: row.template_id ? {
          id: row.template_id,
          name: row.template_name,
          category: row.template_category,
        } : null,
        contract_signatures: row.signers || [],
        contract_provider_tracking: row.provider_tracking || [],
        contract_template_versions: row.version_number != null
          ? { version_number: row.version_number }
          : null,
      })) as Contract[];
    },
    enabled: !!dealId,
    // staleTime: 0 garante que ao abrir a aba Contratos, sempre busque dados frescos
    // do banco, independente do staleTime global de 5 minutos.
    staleTime: 0,
  });
}

/**
 * Hook para criar um contrato (legacy — fluxo principal usa generate_contract_atomic)
 * RELEASE v2.0.0 - 2026-05-10: migrado para RPC create_contract (server-side)
 */
export function useCreateContract() {
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();

  return useMutation({
    mutationFn: async (input: CreateContractInput) => {
      const { data, error } = await supabase.rpc('create_contract', {
        p_payload: {
          deal_id: input.deal_id,
          template_id: input.template_id,
          title: input.title,
          content_html: input.content_html,
          variables_data: input.variables_data,
          used_template_version_id: input.used_template_version_id || null,
          published_contract_id: (input as any).published_contract_id || null,
        } as any,
        p_tenant_hint: input.tenant_id,
      });

      if (error) {
        logger.error('Erro ao criar contrato', error);
        throw error;
      }

      // Retorna shape compatível com o consumidor (precisa de id e deal_id)
      return { id: data as string, deal_id: input.deal_id } as any;
    },
    onSuccess: (data) => {
      toast.success('Contrato criado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['contracts', 'deal', data.deal_id] });
      // v1.1.0: Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });

    },
    onError: (error: any) => {
      logger.error('Falha ao criar contrato', error);
      // O wizard mantém seus tratamentos; aqui apenas exibimos a trava do processo.
      workflowNotice.showWorkflowGuardNotice(error);
      // Não exibe toast genérico aqui — o wizard trata unique violation com mensagem específica
    },
  });
}

/**
 * Hook para atualizar status de um contrato
 * RELEASE v2.0.0 - 2026-05-10: migrado para RPC update_contract_status (server-side)
 */
export function useUpdateContractStatus() {
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();

  return useMutation({
    mutationFn: async ({ contractId, status, dealId }: { contractId: string; status: ContractStatus; dealId: string }) => {
      const { error } = await supabase.rpc('update_contract_status', {
        p_contract_id: contractId,
        p_status: status,
      });

      if (error) {
        logger.error('Erro ao atualizar status do contrato', error);
        throw error;
      }

      return { id: contractId, status, dealId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contracts', 'deal', data.dealId] });
      // v1.1.0: Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });

    },
    onError: (error: any) => {
      // Envio/transição de contrato pode ser bloqueado pela trava do processo.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      logger.error('Falha ao atualizar status do contrato', error);
    },
  });
}

/**
 * Hook para adicionar signatários a um contrato
 */
export function useAddContractSignatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contractId, tenantId, signers, dealId }: {
      contractId: string;
      tenantId: string;
      dealId: string;
      signers: Array<{
        signer_name: string;
        signer_email?: string;
        signer_phone?: string;
        signer_role: string;
        sign_order: number;
      }>;
    }) => {
      /** Monta rows com delivery_method persistido em provider_data */
      const rows = signers.map((s) => ({
        contract_id: contractId,
        tenant_id: tenantId,
        signer_name: s.signer_name,
        signer_email: s.signer_email || null,
        signer_phone: s.signer_phone
          ? (() => {
              // Normaliza telefone para formato E.164 antes de salvar no banco
              const normalized = normalizePhoneIdentifier(s.signer_phone);
              return normalized ? (normalized.startsWith('+') ? normalized : `+${normalized}`) : null;
            })()
          : null,
        signer_role: s.signer_role,
        sign_order: s.sign_order,
        provider_data: { delivery_method: String((s as Record<string, unknown>).delivery_method || 'email') } as Record<string, string>,
      }));

      const { data, error } = await supabase
        .from('contract_signatures')
        .insert(rows)
        .select();

      if (error) {
        logger.error('Erro ao adicionar signatários', error);
        throw error;
      }

      return { data, dealId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['contracts', 'deal', result.dealId] });
    },
  });
}

// =====================================================================
// Hook atômico: gera contrato + signatários + documentos em uma única RPC
// Substitui chamadas separadas a useCreateContract + useAddContractSignatures
// RELEASE: v1.0.0 - 2026-04-09
// =====================================================================

/** Input para geração atômica de contrato */
interface GenerateContractAtomicInput {
  deal_id: string;
  template_id: string;
  title: string;
  content_html: string;
  variables_data: Record<string, string>;
  used_template_version_id?: string | null;
  published_contract_id?: string | null;
  /** Array de signatários com dados de nome, contato e método de entrega */
  signers: Array<{
    signer_name: string;
    signer_email?: string;
    signer_phone?: string;
    signer_role: string;
    sign_order: number;
    delivery_method: string;
  }>;
  /** Array de snapshots de documentos anexados ao contrato */
  document_snapshots: Array<{
    customer_document_id: string;
    document_type_id: string;
    was_required: boolean;
    snapshot_file_name: string;
    snapshot_storage_path: string;
  }>;
  /**
   * Caminho no Storage do PDF pronto do modelo (opcional).
   * Quando informado, backend cria contrato já em status 'generated' apontando
   * para esse PDF, sem passar pela fila de renderização.
   * @release v2026-05
   */
  source_pdf_storage_path?: string | null;
  /**
   * Veículos selecionados no wizard — persistidos como snapshot em
   * contract_vehicles na mesma transação de geração do contrato.
   * @release v2026-08 (Fase 7 do módulo de Veículos)
   */
  vehicle_ids?: string[];
}


/**
 * Hook para gerar contrato atomicamente via RPC server-side
 * Contrato + signatários + snapshot de documentos em transação única
 */
export function useGenerateContractAtomic() {
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();

  return useMutation({
    mutationFn: async (input: GenerateContractAtomicInput) => {
      // Normaliza telefones dos signatários antes de enviar à RPC
      const normalizedSigners = input.signers
        .filter((s) => s.signer_name.trim())
        .map((s) => ({
          ...s,
          signer_phone: s.signer_phone
            ? (() => {
                const normalized = normalizePhoneIdentifier(s.signer_phone);
                return normalized ? (normalized.startsWith('+') ? normalized : `+${normalized}`) : null;
              })()
            : null,
        }));

      const { data, error } = await supabase.rpc('generate_contract_atomic', {
        p_deal_id: input.deal_id,
        p_template_id: input.template_id,
        p_title: input.title,
        p_content_html: input.content_html,
        p_variables_data: input.variables_data as any,
        p_used_template_version_id: input.used_template_version_id || null,
        p_published_contract_id: input.published_contract_id || null,
        p_signers: normalizedSigners as any,
        p_document_snapshots: input.document_snapshots as any,
        // v2026-05: quando modelo é 'uploaded_pdf', backend pula fila de renderização
        p_source_pdf_storage_path: input.source_pdf_storage_path || null,
        // v2026-08: snapshot atômico dos veículos vinculados ao contrato
        p_vehicle_ids: input.vehicle_ids && input.vehicle_ids.length > 0 ? input.vehicle_ids : null,
      } as any);

      if (error) {
        logger.error('Erro ao gerar contrato atômico', error);
        throw error;
      }

      return { contractId: data as string, dealId: input.deal_id };

    },
    onSuccess: (result) => {
      toast.success('Contrato gerado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['contracts', 'deal', result.dealId] });
      // Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
    },

    onError: (error: any) => {
      logger.error('Falha na geração atômica do contrato', error);
      // O wizard mantém seus tratamentos; aqui apenas exibimos a trava do processo.
      workflowNotice.showWorkflowGuardNotice(error);
      // Não exibe toast genérico — o wizard trata erros específicos
    },
  });
}
